import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  FileText, Plus, Edit, Trash2, Loader2, Eye, Users, CheckCircle, Clock,
  GripVertical, Type, AlignLeft, CheckSquare, Calendar, List, Send, BarChart3
} from 'lucide-react';

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text', icon: Type },
  { value: 'textarea', label: 'Long Text', icon: AlignLeft },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'select', label: 'Dropdown', icon: List },
  { value: 'email', label: 'Email', icon: Type },
  { value: 'phone', label: 'Phone', icon: Type },
  { value: 'number', label: 'Number', icon: Type },
];

const FORM_TYPES = [
  { value: 'waiver', label: 'Waiver' },
  { value: 'medical', label: 'Medical Info' },
  { value: 'consent', label: 'Consent Form' },
  { value: 'registration', label: 'Registration' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'other', label: 'Other' },
];

export default function AdminFormsPage() {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('templates');

  // Template builder
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', form_type: 'waiver', fields: [],
    target_roles: ['parent'], trigger_on_enrollment: false,
    trigger_on_signup: false, trigger_manual: true, required: true, active: true,
  });

  // Tracking
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingTemplate, setTrackingTemplate] = useState(null);

  // Assign
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTemplate, setAssignTemplate] = useState(null);
  const [assignRole, setAssignRole] = useState('parent');

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try { const r = await api.get('/forms/manage/'); setTemplates(r.data.results || r.data); }
    catch {} finally { setLoading(false); }
  };

  const openBuilder = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setForm({
        name: template.name, description: template.description || '',
        form_type: template.form_type, fields: template.fields || [],
        target_roles: template.target_roles || ['parent'],
        trigger_on_enrollment: template.trigger_on_enrollment,
        trigger_on_signup: template.trigger_on_signup,
        trigger_manual: template.trigger_manual,
        required: template.required, active: template.active,
      });
    } else {
      setEditingTemplate(null);
      setForm({
        name: '', description: '', form_type: 'waiver', fields: [],
        target_roles: ['parent'], trigger_on_enrollment: false,
        trigger_on_signup: false, trigger_manual: true, required: true, active: true,
      });
    }
    setBuilderOpen(true);
  };

  const addField = () => {
    setForm(prev => ({
      ...prev,
      fields: [...prev.fields, { id: Date.now().toString(), label: '', type: 'text', required: false, options: [] }]
    }));
  };

  const updateField = (index, updates) => {
    setForm(prev => ({
      ...prev,
      fields: prev.fields.map((f, i) => i === index ? { ...f, ...updates } : f)
    }));
  };

  const removeField = (index) => {
    setForm(prev => ({ ...prev, fields: prev.fields.filter((_, i) => i !== index) }));
  };

  const saveTemplate = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/forms/manage/${editingTemplate.id}/`, form);
        toast.success('Form template updated');
      } else {
        await api.post('/forms/manage/', form);
        toast.success('Form template created');
      }
      setBuilderOpen(false);
      fetchTemplates();
    } catch (err) {
      const data = err.response?.data;
      let msg = 'Failed to save';
      if (data) {
        if (data.detail) msg = data.detail;
        else if (typeof data === 'object') msg = Object.entries(data).map(([k,v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('; ');
      }
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this form template?')) return;
    try { await api.delete(`/forms/manage/${id}/`); toast.success('Deleted'); fetchTemplates(); }
    catch { toast.error('Failed to delete'); }
  };

  const openTracking = async (template) => {
    setTrackingTemplate(template);
    setTrackingOpen(true);
    try {
      const r = await api.get(`/forms/manage/${template.id}/tracking/`);
      setTrackingData(r.data);
    } catch { setTrackingData(null); }
  };

  const assignForm = async () => {
    try {
      const r = await api.post(`/forms/manage/${assignTemplate.id}/assign/`, { role: assignRole, include_learners: assignRole === 'parent' });
      toast.success(`Assigned to ${r.data.assigned} users`);
      setAssignOpen(false);
      fetchTemplates();
    } catch { toast.error('Failed to assign'); }
  };

  const toggleRole = (role) => {
    setForm(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><FileText className="w-6 h-6" /> Forms & Waivers</h1>
          <p className="text-muted-foreground">Create form templates, assign to users, and track completion</p>
        </div>
        <Button onClick={() => openBuilder()}><Plus className="h-4 w-4 mr-2" /> Create Form</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{templates.length}</p>
          <p className="text-xs text-muted-foreground">Form Templates</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{templates.filter(t => t.active).length}</p>
          <p className="text-xs text-muted-foreground">Active</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{templates.reduce((s, t) => s + (t.submission_count || 0), 0)}</p>
          <p className="text-xs text-muted-foreground">Total Submissions</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{templates.reduce((s, t) => s + (t.pending_count || 0), 0)}</p>
          <p className="text-xs text-muted-foreground">Pending</p>
        </CardContent></Card>
      </div>

      {/* Templates List */}
      {templates.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">
          <FileText className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold">No form templates yet</p>
          <p className="text-sm">Create your first form template to get started</p>
          <Button className="mt-4" onClick={() => openBuilder()}><Plus className="h-4 w-4 mr-2" /> Create Form</Button>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {templates.map(t => (
            <Card key={t.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{t.name}</h3>
                      {!t.active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                      {t.required && <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{t.description}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline">{t.form_type}</Badge>
                      <Badge variant="secondary">{(t.fields || []).length} fields</Badge>
                      {(t.target_roles || []).map(r => <Badge key={r} className="bg-blue-100 text-blue-700 text-xs">{r}</Badge>)}
                      {t.trigger_on_enrollment && <Badge className="bg-green-100 text-green-700 text-xs">On Enrollment</Badge>}
                      {t.trigger_on_signup && <Badge className="bg-purple-100 text-purple-700 text-xs">On Signup</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t.submission_count || 0} submissions · {t.pending_count || 0} pending
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openTracking(t)} title="Track completion"><BarChart3 className="h-4 w-4 text-blue-500" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => { setAssignTemplate(t); setAssignOpen(true); }} title="Assign"><Send className="h-4 w-4 text-green-500" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => openBuilder(t)} title="Edit"><Edit className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => deleteTemplate(t.id)} title="Delete"><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* TEMPLATE BUILDER DIALOG */}
      <Dialog open={builderOpen} onOpenChange={setBuilderOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Form Template' : 'Create Form Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Form Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Photo Release Waiver" />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="What is this form for?" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Form Type</Label>
                <Select value={form.form_type} onValueChange={v => setForm(p => ({...p, form_type: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FORM_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Version</Label>
                <Input value={form.version || '1.0'} onChange={e => setForm(p => ({...p, version: e.target.value}))} />
              </div>
            </div>

            {/* Target Roles */}
            <div className="space-y-2">
              <Label>Who fills this form?</Label>
              <div className="flex gap-3">
                {['parent', 'instructor', 'volunteer'].map(role => (
                  <button key={role} onClick={() => toggleRole(role)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                      form.target_roles.includes(role) ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-500'
                    }`}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Triggers */}
            <div className="space-y-3">
              <Label>When is this form triggered?</Label>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div><p className="font-medium text-sm">On Enrollment</p><p className="text-xs text-muted-foreground">Auto-assign when a learner enrolls in a course</p></div>
                  <Switch checked={form.trigger_on_enrollment} onCheckedChange={v => setForm(p => ({...p, trigger_on_enrollment: v}))} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div><p className="font-medium text-sm">On Account Signup</p><p className="text-xs text-muted-foreground">Auto-assign when a new account is created</p></div>
                  <Switch checked={form.trigger_on_signup} onCheckedChange={v => setForm(p => ({...p, trigger_on_signup: v}))} />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div><p className="font-medium text-sm">Required</p><p className="text-xs text-muted-foreground">Must be completed before proceeding</p></div>
                  <Switch checked={form.required} onCheckedChange={v => setForm(p => ({...p, required: v}))} />
                </div>
              </div>
            </div>

            {/* Form Fields Builder */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Form Fields ({form.fields.length})</Label>
                <Button variant="outline" size="sm" onClick={addField}><Plus className="h-4 w-4 mr-1" /> Add Field</Button>
              </div>
              {form.fields.length === 0 && (
                <div className="p-6 border-2 border-dashed rounded-lg text-center text-muted-foreground">
                  <p>No fields yet. Click "Add Field" to start building your form.</p>
                </div>
              )}
              {form.fields.map((field, i) => {
                const FieldIcon = FIELD_TYPES.find(t => t.value === field.type)?.icon || Type;
                return (
                  <div key={field.id || i} className="p-3 border rounded-lg bg-gray-50 space-y-2">
                    <div className="flex items-center gap-2">
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                      <FieldIcon className="h-4 w-4 text-muted-foreground" />
                      <Input value={field.label} onChange={e => updateField(i, { label: e.target.value })}
                        placeholder="Field label (e.g., Child's Allergies)" className="flex-1 h-8" />
                      <Select value={field.type} onValueChange={v => updateField(i, { type: v })}>
                        <SelectTrigger className="w-[130px] h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{FIELD_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <label className="flex items-center gap-1 text-xs whitespace-nowrap">
                        <input type="checkbox" checked={field.required} onChange={e => updateField(i, { required: e.target.checked })} className="rounded" />
                        Required
                      </label>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeField(i)}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    </div>
                    {field.type === 'select' && (
                      <Input value={(field.options || []).join(', ')} onChange={e => updateField(i, { options: e.target.value.split(',').map(s => s.trim()) })}
                        placeholder="Options (comma-separated): Option 1, Option 2, Option 3" className="h-8 text-xs" />
                    )}
                    {(field.type === 'text' || field.type === 'textarea') && (
                      <Input value={field.placeholder || ''} onChange={e => updateField(i, { placeholder: e.target.value })}
                        placeholder="Placeholder text (optional)" className="h-8 text-xs" />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuilderOpen(false)}>Cancel</Button>
            <Button onClick={saveTemplate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingTemplate ? 'Save Changes' : 'Create Form'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TRACKING DIALOG */}
      <Dialog open={trackingOpen} onOpenChange={setTrackingOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Completion Tracking — {trackingTemplate?.name}</DialogTitle></DialogHeader>
          {trackingData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 bg-gray-50 rounded"><p className="text-xl font-bold">{trackingData.total}</p><p className="text-xs">Assigned</p></div>
                <div className="p-3 bg-green-50 rounded"><p className="text-xl font-bold text-green-600">{trackingData.completed}</p><p className="text-xs">Completed</p></div>
                <div className="p-3 bg-yellow-50 rounded"><p className="text-xl font-bold text-yellow-600">{trackingData.pending}</p><p className="text-xs">Pending</p></div>
              </div>
              {trackingData.pending_list?.length > 0 && (
                <div><h4 className="font-semibold mb-2 text-yellow-700">Pending ({trackingData.pending})</h4>
                  <div className="space-y-1">{trackingData.pending_list.map((p, i) => (
                    <div key={i} className="flex justify-between text-sm p-2 bg-yellow-50 rounded">
                      <span>{p.user} {p.learner ? `(for ${p.learner})` : ''}</span><span className="text-xs text-muted-foreground">{p.email}</span>
                    </div>
                  ))}</div>
                </div>
              )}
              {trackingData.completed_list?.length > 0 && (
                <div><h4 className="font-semibold mb-2 text-green-700">Completed ({trackingData.completed})</h4>
                  <div className="space-y-1">{trackingData.completed_list.map((c, i) => (
                    <div key={i} className="flex justify-between text-sm p-2 bg-green-50 rounded">
                      <span>{c.user} {c.learner ? `(for ${c.learner})` : ''}</span><span className="text-xs text-muted-foreground">{c.completed_at?.split('T')[0]}</span>
                    </div>
                  ))}</div>
                </div>
              )}
              {trackingData.total === 0 && <p className="text-center text-muted-foreground py-4">No assignments yet. Use the "Assign" button to send this form to users.</p>}
            </div>
          ) : <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>}
        </DialogContent>
      </Dialog>

      {/* ASSIGN DIALOG */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign — {assignTemplate?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Assign to role</Label>
              <Select value={assignRole} onValueChange={setAssignRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="parent">All Parents</SelectItem>
                  <SelectItem value="instructor">All Instructors</SelectItem>
                  <SelectItem value="volunteer">All Volunteers</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-muted-foreground">
              {assignRole === 'parent' ? 'Each parent will receive one form per learner.' : 'Each user will receive one form.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={assignForm}><Send className="h-4 w-4 mr-2" /> Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
